import brandLogo from "@/assets/hustlemate-logo.png";
import { durationLabel, flexCategoryIconUrl, PALETTES, PROVENANCE } from "./flexStudioCanvas";
import { drawFocusTimeline } from "./focusTimelineCanvas";

export const FOCUS_PALETTES = {
  reference: { focus: "#FF7A1A", break: "#D9DEE7" },
  ...PALETTES,
};

// Focus overlays have their own layout: task stamps retain their existing design.
export const FOCUS_ELEMENTS = {
  icon: "Focus icon", timeline: "Timeline", headline: "Main caption", caption: "Session caption",
  duration: "Focused time", breaks: "Break duration", longest: "Longest block", added: "Extra time",
  legend: "Timeline colour labels", timelineLabel: "Timeline description", username: "Username",
  date: "Date", time: "Time", timezone: "Time zone", provenance: "Verification label", brand: "Logo & name",
};
const BOXES = {
  icon: [390, 36, 300, 300], headline: [90, 362, 900, 38], caption: [90, 409, 900, 32],
  timeline: [90, 462, 900, 210], duration: [90, 706, 205, 58], breaks: [320, 706, 205, 58],
  longest: [550, 706, 205, 58], added: [780, 706, 205, 58], legend: [90, 674, 500, 28],
  timelineLabel: [90, 438, 900, 24], username: [90, 816, 500, 32], date: [90, 862, 260, 25],
  time: [372, 862, 180, 25], timezone: [580, 862, 410, 25], provenance: [90, 917, 630, 20],
  brand: [790, 913, 200, 25],
};
const CLOCK_BOXES = {
  timeline: [240, 430, 600, 360],
  duration: [410, 480, 260, 62],
  breaks: [410, 560, 260, 62],
  longest: [410, 640, 260, 62],
  legend: [390, 790, 400, 28],
};
export function focusDefaults() {
  const on = new Set(["icon", "timeline", "headline", "caption", "duration", "legend", "provenance", "brand"]);
  return Object.fromEntries(Object.keys(FOCUS_ELEMENTS).map((key) => [key, { visible: on.has(key), x: 0, y: 0, scale: 1 }]));
}
export function focusBox(key, layer = {}, format = "sticker", timelineStyle = "line") {
  const [x, y, w, h] = timelineStyle === "clock" && CLOCK_BOXES[key] ? CLOCK_BOXES[key] : BOXES[key];
  const height = format === "story" ? 1920 : format === "square" ? 1080 : 960;
  const scale = Math.max(.5, Math.min(1.3, 1056 / w, (height - 24) / h, Number(layer.scale) || 1));
  return { x: Math.max(12, Math.min(1068 - w * scale, x + (Number(layer.x) || 0))),
    y: Math.max(12, Math.min(height - 12 - h * scale, y + (height - 960) / 2 + (Number(layer.y) || 0))), w: w * scale, h: h * scale, scale };
}
const imageCache = new Map();

// A time-contour, not an activity waveform: distance along the trail represents
// elapsed time. The fixed curve and uniform ribs never imply measured intensity.
export function drawFocusTrail(ctx, box, segments, focusColor, breakColor, recorded) {
  const valid = segments.filter((item) => Number.isFinite(item.seconds) && item.seconds > 0);
  const total = valid.reduce((sum, item) => sum + item.seconds, 0);
  if (!total) return;
  const { x, y, w, h, scale } = box;
  const points = []; let length = 0;
  for (let i = 0; i <= 600; i++) {
    const t = i / 600;
    const p = { x: x + w * (.03 + .94 * t), y: y + h * (.5 - .32 * Math.sin(t * Math.PI * 2) * Math.sin(Math.PI * t)), length: 0 };
    if (i) length += Math.hypot(p.x - points[i - 1].x, p.y - points[i - 1].y);
    p.length = length; points.push(p);
  }
  const at = (fraction) => {
    const distance = Math.max(0, Math.min(1, fraction)) * length;
    let lo = 1, hi = points.length - 1;
    while (lo < hi) { const mid = (lo + hi) >> 1; if (points[mid].length < distance) lo = mid + 1; else hi = mid; }
    const a = points[lo - 1], b = points[lo], mix = (distance - a.length) / Math.max(.001, b.length - a.length);
    return { x: a.x + (b.x - a.x) * mix, y: a.y + (b.y - a.y) * mix, angle: Math.atan2(b.y - a.y, b.x - a.x) };
  };
  ctx.save(); ctx.lineCap = "round";
  let cursor = 0;
  valid.forEach((segment) => {
    const start = cursor, end = cursor + segment.seconds / total; cursor = end;
    const isFocus = segment.kind === "focus";
    ctx.strokeStyle = isFocus ? focusColor : breakColor;
    // Continuous fine spine preserves even very short intervals; ribs add texture.
    ctx.lineWidth = (isFocus ? 3 : 2) * scale;
    ctx.beginPath();
    const samples = Math.max(2, Math.ceil((end - start) * 600));
    for (let j = 0; j <= samples; j++) { const p = at(start + (end - start) * j / samples); if (!j) ctx.moveTo(p.x, p.y); else ctx.lineTo(p.x, p.y); }
    ctx.stroke();
    const spacing = 7 * scale / length;
    for (let f = (Math.ceil(start / spacing) + .5) * spacing; f < end; f += spacing) {
      const p = at(f), half = (isFocus ? 19 : 7) * scale;
      const nx = -Math.sin(p.angle), ny = Math.cos(p.angle);
      ctx.lineWidth = (isFocus ? 2.3 : 2) * scale;
      ctx.beginPath(); ctx.moveTo(p.x - nx * half, p.y - ny * half); ctx.lineTo(p.x + nx * half, p.y + ny * half); ctx.stroke();
    }
    // Small outlined pause nodes distinguish rests without oversized blocks.
    if (!isFocus && (end - start) * length > 24 * scale) {
      const p = at((start + end) / 2);
      ctx.lineWidth = 2 * scale; ctx.beginPath(); ctx.arc(p.x, p.y, 11 * scale, 0, Math.PI * 2); ctx.stroke();
    }
  });
  // Start is hollow; finish is a double-ring endpoint. These are not map pins.
  (recorded ? [0, 1] : []).forEach((fraction) => {
    const p = at(fraction), segment = fraction ? valid[valid.length - 1] : valid[0];
    ctx.strokeStyle = segment.kind === "focus" ? focusColor : breakColor;
    ctx.lineWidth = 2 * scale; ctx.beginPath(); ctx.arc(p.x, p.y, 8 * scale, 0, Math.PI * 2); ctx.stroke();
    if (fraction) { ctx.beginPath(); ctx.arc(p.x, p.y, 3 * scale, 0, Math.PI * 2); ctx.stroke(); }
  });
  // No chronological endpoint claims are added to totals-only historical data.
  ctx.restore();
}
function loadImage(src) {
  if (!src) return Promise.resolve(null);
  const create = () => new Promise((resolve) => {
    const img = new Image(), timeout = setTimeout(() => resolve(null), 8000);
    img.onload = () => { clearTimeout(timeout); resolve(img); };
    img.onerror = () => { clearTimeout(timeout); resolve(null); };
    img.src = src;
  });
  // Local photos can be large and are revoked when replaced; do not retain them.
  if (src.startsWith("blob:")) return create();
  if (!imageCache.has(src)) imageCache.set(src, create());
  return imageCache.get(src);
}
export async function renderFocusFlex(canvas, { stamp, username, timeZone = "UTC", layout = focusDefaults(), headline = "Staying focused is a flex.", caption = stamp.title || "Build something that matters", format = "sticker", palette = "reference", timelineStyle = "line", background = "transparent", photo, ink = "light" }) {
  canvas.width = 1080; canvas.height = format === "story" ? 1920 : format === "square" ? 1080 : 960;
  const ctx = canvas.getContext("2d"), colors = FOCUS_PALETTES[palette] || FOCUS_PALETTES.reference;
  const [icon, logo, bg] = await Promise.all([loadImage(flexCategoryIconUrl("focus", stamp.provenance)), loadImage(brandLogo), background === "photo" ? loadImage(photo) : null]);
  if (background !== "transparent") {
    ctx.fillStyle = "#101114"; ctx.fillRect(0, 0, canvas.width, canvas.height);
    if (bg) { const s = Math.max(canvas.width / bg.width, canvas.height / bg.height); ctx.drawImage(bg, (canvas.width - bg.width * s) / 2, (canvas.height - bg.height * s) / 2, bg.width * s, bg.height * s); }
  }
  ctx.textBaseline = "middle";
  const foreground = ink === "dark" ? "#17181B" : "#FAFAF7";
  const write = (value, box, size = 23, centered = false, color = foreground) => {
    let n = size * box.scale; ctx.font = `500 ${n}px Arial, sans-serif`;
    while (ctx.measureText(value).width > box.w && n > 12) { n--; ctx.font = `500 ${n}px Arial, sans-serif`; }
    ctx.fillStyle = color; ctx.textAlign = centered ? "center" : "left";
    ctx.fillText(value, centered ? box.x + box.w / 2 : box.x, box.y + box.h / 2, box.w);
  };
  const when = (options) => {
    if (!stamp.earnedAt || !Number.isFinite(new Date(stamp.earnedAt).getTime())) return "";
    try { return new Intl.DateTimeFormat(undefined, { ...options, timeZone }).format(new Date(stamp.earnedAt)); }
    catch { return new Intl.DateTimeFormat(undefined, { ...options, timeZone: "UTC" }).format(new Date(stamp.earnedAt)); }
  };
  for (const key of Object.keys(FOCUS_ELEMENTS)) {
    if (!(layout[key] || focusDefaults()[key]).visible) continue;
    const b = focusBox(key, layout[key], format, timelineStyle);
    if (key === "icon") {
      if (icon) { const s = Math.min(b.w / icon.width, b.h / icon.height); ctx.drawImage(icon, b.x + (b.w - icon.width * s) / 2, b.y + (b.h - icon.height * s) / 2, icon.width * s, icon.height * s); }
      else { ctx.strokeStyle = colors.focus; ctx.lineWidth = 7; [0.4, .25, .08].forEach((r) => { ctx.beginPath(); ctx.arc(b.x + b.w / 2, b.y + b.h / 2, b.w * r, 0, Math.PI * 2); ctx.stroke(); }); }
    } else if (key === "timeline") {
      const segments = stamp.timelineQuality === "recorded" && stamp.timeline?.length ? stamp.timeline : [{ kind: "focus", seconds: stamp.focusedSeconds }, { kind: "break", seconds: stamp.breakSeconds }];
      drawFocusTimeline(ctx, b, segments, timelineStyle, ink === "dark" && palette === "ice" ? "#24262B" : colors.focus, colors.break, stamp.timelineQuality === "recorded", drawFocusTrail);
    } else if (key === "brand") {
      if (logo) ctx.drawImage(logo, b.x, b.y, b.h, b.h);
      write("HustlrMate", { ...b, x: b.x + b.h + 9, w: b.w - b.h - 9 }, 20);
    } else if (key === "legend") {
      const swatch = (left, color, label) => {
        const side = 13 * b.scale;
        ctx.fillStyle = color; ctx.fillRect(left, b.y + (b.h - side) / 2, side, side);
        write(label, { ...b, x: left + side + 10 * b.scale, w: b.w * .29 }, 17);
      };
      swatch(b.x, ink === "dark" && palette === "ice" ? "#24262B" : colors.focus, "Focus");
      swatch(b.x + b.w * .36, colors.break, "Break");
    } else if (["duration", "breaks", "longest", "added"].includes(key)) {
      const number = {
        duration: durationLabel(stamp.focusedSeconds), breaks: durationLabel(stamp.breakSeconds),
        longest: stamp.longestBlockSeconds == null ? "—" : durationLabel(stamp.longestBlockSeconds),
        added: `+${stamp.extraMinutes || 0}m`,
      }[key];
      const label = { duration: "FOCUS", breaks: "BREAKS", longest: "LONGEST BLOCK", added: "TIME ADDED" }[key];
      if (timelineStyle === "clock" && key !== "added") {
        if (key !== "duration") {
          const lineWidth = 92 * b.scale;
          ctx.strokeStyle = `${foreground}24`; ctx.lineWidth = 1 * b.scale;
          ctx.beginPath(); ctx.moveTo(b.x + (b.w - lineWidth) / 2, b.y); ctx.lineTo(b.x + (b.w + lineWidth) / 2, b.y); ctx.stroke();
        }
        write(number, { ...b, y: b.y + 6 * b.scale, h: 31 * b.scale }, 26, true);
        write(label, { ...b, y: b.y + 38 * b.scale, h: 16 * b.scale }, 11, true, `${foreground}A8`);
      } else {
        ctx.strokeStyle = `${foreground}55`; ctx.lineWidth = 1 * b.scale;
        ctx.beginPath(); ctx.moveTo(b.x, b.y); ctx.lineTo(b.x + b.w, b.y); ctx.stroke();
        write(number, { ...b, y: b.y + 11 * b.scale, h: 34 * b.scale }, 29);
        write(label, { ...b, y: b.y + 43 * b.scale, h: 17 * b.scale }, 13, false, `${foreground}B8`);
      }
    } else {
      const values = { headline, caption, timelineLabel: stamp.timelineQuality === "recorded" ? "Focus & breaks · in order" : "Time split · interval history unavailable", username: username ? `@${username}` : "", date: when({ dateStyle: "medium" }), time: when({ timeStyle: "short" }), timezone: timeZone, provenance: PROVENANCE[stamp.provenance] || PROVENANCE.solo };
      write(String(values[key] || ""), b, key === "headline" ? 30 : key === "provenance" || key === "timelineLabel" ? 15 : key === "date" || key === "time" || key === "timezone" ? 18 : 23, key === "headline" || key === "caption", key === "date" || key === "time" || key === "timezone" || key === "provenance" ? `${foreground}B8` : foreground);
    }
  }
}
